import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPalette, faStar, faTrophy } from '@fortawesome/free-solid-svg-icons';
import GalleryCountdown from '../shared/GalleryCountdown';
import './SketcherArtGallery.css';

interface Props {
  results: SketcherRoundResult[] | null;
  roundNumber: number;
  totalRounds: number;
  expiryTime?: string | null;
}

const SketcherArtGallery: React.FC<Props> = ({
  results,
  roundNumber,
  totalRounds,
  expiryTime,
}) => {
  const isWaiting = !results || results.length === 0;

  // Highest score = lowest countDown. Intent: show the highest score LAST,
  // so sort by countDown DESCENDING — the winner (countDown 1) ends up last.
  const sortedResults = results
    ? [...results].sort((a, b) => b.countDown - a.countDown)
    : [];

  return (
    <div className="sag">
      {isWaiting ? (
        <div className="sag__waiting">
          <div className="sag__easel">
            <div className="sag__easel-canvas">
              <FontAwesomeIcon icon={faPalette} className="sag__palette-icon" />
            </div>
            <div className="sag__easel-leg sag__easel-leg--left" />
            <div className="sag__easel-leg sag__easel-leg--right" />
          </div>
          <div className="sag__loading-dots">
            <span />
            <span />
            <span />
          </div>
          <h2 className="sag__waiting-title">Preparing Art Gallery...</h2>
          <p className="sag__waiting-sub">
            Our judges are reviewing the masterpieces 🎨
          </p>
        </div>
      ) : (
        <div className="sag__gallery">
          <div className="sag__gallery-header">
            <h2 className="sag__gallery-title">
              <FontAwesomeIcon icon={faPalette} /> Art Gallery
            </h2>
            <div className="sag__gallery-header-right">
              <GalleryCountdown expiryTime={expiryTime} />
              <span className="sag__gallery-round">
                Round {roundNumber}/{totalRounds}
              </span>
            </div>
          </div>

          <div className="sag__grid">
            {sortedResults.map((r, idx) => {
              const isWinner = r.countDown === 1;
              return (
                <div
                  key={r.playerId}
                  className={`sag__card ${isWinner ? 'sag__card--winner' : ''}`}
                  style={{ animationDelay: `${idx * 0.12}s` }}
                >
                  {isWinner && (
                    <span className="sag__card-trophy">
                      <FontAwesomeIcon icon={faTrophy} />
                    </span>
                  )}
                  <div className="sag__card-img-wrap">
                    <img
                      src={r.imageUrl}
                      alt={`${r.playerName}'s sketch`}
                      className="sag__card-img"
                    />
                  </div>
                  <div className="sag__card-info">
                    <span className="sag__card-rank">#{r.countDown}</span>
                    <span className="sag__card-name">{r.playerName}</span>
                    <span className="sag__card-message">
                      {r.rankingMessage}
                    </span>
                    <div className="sag__card-stats">
                      <span className="sag__card-score">
                        <FontAwesomeIcon icon={faStar} /> {r.score} pts
                      </span>
                      <span className="sag__card-time">{r.timeTaken}s</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default SketcherArtGallery;
